/**
 * use-quick-list — TanStack Query hook dla listy podręcznej (pin/unpin),
 * wzorowanej na mechanizmie ESITE.WorkFlow (F20 T20.x).
 *
 * Backend (`GET/POST /api/quick-list`, `DELETE /api/quick-list/:id`) zwraca
 * WYŁĄCZNIE gołe referencje (entityType/entityId) własnych przypięć requestującego
 * usera — bez hydratacji encji docelowej (świadoma granica bezpieczeństwa, patrz
 * komentarz w apps/api/app/api/quick-list/route.ts). Ten hook NIE dogrywa danych
 * encji — callerzy łączą `pinnedIds` z własną listą (np. useDocumentTypes) po
 * stronie renderu.
 *
 * WAŻNE: DELETE działa po własnym `id` wiersza quick_list_items, NIE po
 * entityId — stąd hook trzyma obok `Set<entityId>` (do O(1) „czy przypięte")
 * także `Map<entityId, quickListItemId>` (do znalezienia id wiersza przy unpin).
 * Obie struktury derywowane z tego samego query result — jedno zapytanie GET.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export type QuickListEntityType = 'document_type';

interface QuickListItem {
  id: string;
  entityType: QuickListEntityType;
  entityId: string;
  createdAt: string;
}

interface QuickListResponse {
  items: QuickListItem[];
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useQuickList(entityType: QuickListEntityType): {
  pinnedIds: Set<string>;
  isLoading: boolean;
  pin: (entityId: string) => void;
  unpin: (entityId: string) => void;
  isPinning: boolean;
  isUnpinning: boolean;
} {
  const queryClient = useQueryClient();
  const queryKey = ['quick-list', entityType] as const;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get<QuickListResponse>(
        `/quick-list?entityType=${encodeURIComponent(entityType)}`,
      );
      return res.data.items;
    },
    staleTime: 30_000,
  });

  const items = useMemo(() => query.data ?? [], [query.data]);

  const pinnedIds = useMemo(() => new Set(items.map((item) => item.entityId)), [items]);

  const idByEntityId = useMemo(
    () => new Map(items.map((item) => [item.entityId, item.id])),
    [items],
  );

  const pinMutation = useMutation({
    mutationFn: async (entityId: string) => {
      await api.post('/quick-list', { entityType, entityId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const unpinMutation = useMutation({
    mutationFn: async (entityId: string) => {
      const itemId = idByEntityId.get(entityId);
      if (!itemId) return;
      await api.delete(`/quick-list/${itemId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    pinnedIds,
    isLoading: query.isLoading,
    pin: (entityId: string) => pinMutation.mutate(entityId),
    unpin: (entityId: string) => unpinMutation.mutate(entityId),
    isPinning: pinMutation.isPending,
    isUnpinning: unpinMutation.isPending,
  };
}
